import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { LoadFailed } from "@/components/LoadState";
import { Briefcase, ExternalLink } from "lucide-react";

// Read-only list of Business & Dealer waitlist leads for the admin "Waitlist" tab. No workflow —
// just a list to work from manually (name / email / business / location / website / submitted).
export const BusinessWaitlistTable = () => {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const load = () => {
    setErr(null);
    api.get("/admin/business-waitlist")
      .then(({ data }) => setRows(data.items || []))
      .catch((e) => { setErr(e?.message || "load failed"); setRows([]); });
  };
  useEffect(() => { load(); }, []);

  if (err) return (
    <div className="mt-5 border border-sniper bg-surface" data-testid="business-waitlist-table">
      <LoadFailed reason={err} onRetry={load} testid="business-waitlist-load-failed" />
    </div>
  );
  if (rows === null) return <div className="mt-5 font-mono text-xs text-muted-foreground" data-testid="business-waitlist-loading">Loading waitlist…</div>;
  if (rows.length === 0) return (
    <div className="mt-5 border border-sniper bg-surface p-10 text-center" data-testid="business-waitlist-empty">
      <Briefcase className="w-8 h-8 text-muted-foreground/40 mx-auto mb-3" strokeWidth={1.4} />
      <p className="font-heading uppercase tracking-tight text-lg text-foreground">No waitlist signups yet</p>
      <p className="font-mono text-[11px] text-muted-foreground mt-1 max-w-md mx-auto">When someone joins the Business &amp; Dealer waitlist, they'll appear here with their contact details.</p>
    </div>
  );

  return (
    <div className="mt-5 border border-sniper bg-surface sm:overflow-x-auto" data-testid="business-waitlist-table">
      <div className="px-4 py-3 border-b border-sniper font-mono text-[10px] uppercase tracking-wide text-muted-foreground" data-testid="business-waitlist-count">
        {rows.length} on the waitlist
      </div>
      <table className="w-full text-left border-collapse block sm:table sm:min-w-[900px]">
        <thead className="hidden sm:table-header-group">
          <tr className="border-b border-sniper font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
            <th className="px-4 py-3">Name</th>
            <th className="px-4 py-3">Email</th>
            <th className="px-4 py-3">Business</th>
            <th className="px-4 py-3">Location</th>
            <th className="px-4 py-3">Website</th>
            <th className="px-4 py-3">Submitted</th>
          </tr>
        </thead>
        <tbody className="block sm:table-row-group">
          {rows.map((r) => (
            <tr key={r.waitlist_id} className="block sm:table-row border border-sniper sm:border-x-0 sm:border-t-0 sm:border-b sm:border-sniper/60 mb-3 sm:mb-0 hover:bg-white/[0.02] align-top" data-testid={`business-waitlist-row-${r.waitlist_id}`}>
              <td className="block sm:table-cell px-4 py-3 text-sm text-foreground"><span className="sm:hidden font-mono text-[10px] uppercase text-muted-foreground mr-1">Name:</span>{r.name}</td>
              <td className="block sm:table-cell px-4 py-3 font-mono text-[11px] text-secondary-foreground break-all"><span className="sm:hidden text-muted-foreground uppercase mr-1">Email:</span>{r.email}</td>
              <td className="block sm:table-cell px-4 py-3 text-sm text-foreground"><span className="sm:hidden font-mono text-[10px] uppercase text-muted-foreground mr-1">Business:</span>{r.business_name}</td>
              <td className="block sm:table-cell px-4 py-3 font-mono text-[11px] text-secondary-foreground"><span className="sm:hidden text-muted-foreground uppercase mr-1">Location:</span>{r.location}</td>
              <td className="block sm:table-cell px-4 py-3 font-mono text-[11px]">
                {r.website
                  ? <a href={r.website.startsWith("http") ? r.website : `https://${r.website}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-olive-bright hover:underline">{r.website}<ExternalLink className="w-3 h-3" /></a>
                  : <span className="text-muted-foreground">—</span>}
              </td>
              <td className="block sm:table-cell px-4 py-3 font-mono text-[11px] text-secondary-foreground sm:whitespace-nowrap" title={r.submitted_at}>
                <span className="sm:hidden text-muted-foreground uppercase mr-1">Submitted:</span>{r.submitted_at ? new Date(r.submitted_at).toLocaleDateString() : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
